import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { permissionsApi, type GroupSpec, type GroupBadgeColor, type GroupArchetype } from '@/api/permissions'
import type { UserRole } from '@/types'

// Hydrates from /api/permissions/groups on first use. Replaces the
// hardcoded role-picker / badge-color / hierarchy lists that used to be
// duplicated across pages — single source of truth for group display
// metadata (the catalog ceiling/floor lives on the backend).
export const useGroupsStore = defineStore('groups', () => {
  const groups = ref<GroupSpec[]>([])
  const loaded = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function hydrate(force = false): Promise<void> {
    if (loaded.value && !force) return
    if (loading.value) return
    loading.value = true
    error.value = null
    try {
      const response = await permissionsApi.getGroups()
      groups.value = response.groups
      loaded.value = true
    } catch (err: any) {
      error.value = err?.response?.data?.message || 'Failed to load groups'
      throw err
    } finally {
      loading.value = false
    }
  }

  function reset(): void {
    groups.value = []
    loaded.value = false
    error.value = null
  }

  // Indexes — recomputed when `groups` changes.
  const byKey = computed<Record<string, GroupSpec>>(() =>
    Object.fromEntries(groups.value.map((g) => [g.key, g])),
  )

  function spec(key: UserRole | string | null | undefined): GroupSpec | undefined {
    if (!key) return undefined
    return byKey.value[key]
  }

  function label(key: UserRole | string | null | undefined, fallback = ''): string {
    return spec(key)?.label ?? fallback
  }

  function color(key: UserRole | string | null | undefined): GroupBadgeColor {
    return spec(key)?.badge_color ?? 'default'
  }

  function level(key: UserRole | string | null | undefined): number {
    return spec(key)?.level ?? 0
  }

  function archetype(key: UserRole | string | null | undefined): GroupArchetype | undefined {
    return spec(key)?.archetype
  }

  function mobileRoute(key: UserRole | string | null | undefined): string | null {
    return spec(key)?.mobile_route ?? null
  }

  // Role-picker helper: all groups strictly below the caller's level (with
  // optional archetype filter, e.g. only BACK_OFFICE roles for sacco user
  // creation surfaces).
  function rolesBelow(callerLevel: number, options: { archetype?: GroupArchetype } = {}): GroupSpec[] {
    return groups.value
      .filter((g) => g.level < callerLevel)
      .filter((g) => !options.archetype || g.archetype === options.archetype)
      .sort((a, b) => b.level - a.level || a.key.localeCompare(b.key))
  }

  // Roles that can exist inside a sacco (SACCO_ADMIN, MANAGER, FINANCE,
  // INVESTOR, CREW). Excludes PLATFORM archetype (SUPER_ADMIN, PLATFORM_OPS)
  // — those are admin-portal only and must never appear in a sacco user picker.
  // Optionally filter by payment_model so a ma3hub sacco doesn't offer FINANCE
  // (veripay-only role) and a veripay sacco doesn't offer roles that don't
  // exist there.
  function saccoRoles(options: { paymentModel?: string | null } = {}): GroupSpec[] {
    const model = options.paymentModel ?? null
    return groups.value
      .filter((g) => g.archetype !== 'PLATFORM')
      .filter((g) => {
        if (!model) return true
        // FINANCE is veripay-only; INVESTOR is veripay-only.
        if (model === 'ma3hub' && (g.key === 'FINANCE' || g.key === 'INVESTOR')) {
          return false
        }
        return true
      })
      .sort((a, b) => b.level - a.level || a.key.localeCompare(b.key))
  }

  // Roles that can hold an admin-portal account (SUPER_ADMIN, PLATFORM_OPS).
  // For platform user CRUD pages.
  function platformRoles(): GroupSpec[] {
    return groups.value
      .filter((g) => g.archetype === 'PLATFORM')
      .sort((a, b) => b.level - a.level || a.key.localeCompare(b.key))
  }

  return {
    groups,
    loaded,
    loading,
    error,
    hydrate,
    reset,
    spec,
    label,
    color,
    level,
    archetype,
    mobileRoute,
    rolesBelow,
    saccoRoles,
    platformRoles,
  }
})
